import React from "react";
import { PAGE_NAME, PERMISSIONS } from "../../../constants/constants";
import { Icon } from "../../atoms/icon";
import { useGetDashboardDataQuery, useGetNotificationQuery } from "../../../store/dashboard/dashboardSlice";
import { hasPermission } from "../../../utils";
import { Link } from "react-router-dom";
import { ScrollingText } from "../../molecules/scrollingText";
// import { useSelector } from "react-redux";
// import { userStateType } from "../../../types";

const Dashboard = () => {
  const pageClassName = "p-dashboard";
  // const user = useSelector((state: userStateType) => state.user.user);
  const { data, isSuccess } = useGetDashboardDataQuery();



  // Three column dashboard data
  const [threeColumnData, setThreeColumnData] = React.useState({
    column1: [
      { title: "Total Users", value: 0, icon: "user" },
      { title: "Total Organization", value: 0, icon: "organization" },
      { title: "Total Tools & Infra", value: 0, icon: "saas" },
      { title: "Total Mitutoyo Projects", value: 0, icon: "project" },
    ],
    column2: [
      { title: "GitLab Storage Usage", value: "0 GB", icon: "gitlab" },
      { title: `GitLab Compute Credits (${new Date().toLocaleString("default", { month: "long" })})`, value: "0 minutes", month: "", icon: "gitlab" },
      { title: "Total GCP Cost", value: 0, icon: "gcp" },
      { title: "Total AWS Cost", value: 0, icon: "aws" },
    ],
    column3: [
      { title: "Pending Invitations", value: 0, icon: "inviteUsers" },
      { title: "License Queue", value: 0, icon: "license" },
      { title: "Approved Requests", value: 0, icon: "approvedRequest" },
      { title: "Pending Approvals", value: 0, icon: "pendingRequest" },
    ],
  });

  React.useEffect(() => {
    if (isSuccess && data?.result) {
      setThreeColumnData({
        column1: threeColumnData.column1.map((item, index) => ({
          ...item,
          value: data.result[item.title.toLowerCase().replace(/ (.)/g, (match, letter) => letter.toUpperCase())],
        })),
        column2: threeColumnData.column2.map((item, index) => ({
          ...item,
          value: item.title === "GitLab Storage Usage" ? `${data.result.gitlabStorageUsage} GB` : item.title === `GitLab Compute Credits (${new Date().toLocaleString("default", { month: "long" })})` ? `${data.result.gitlabComputeCredits} minutes` : data.result[item.title.toLowerCase().replace(/ (.)/g, (match, letter) => letter.toUpperCase())],
        })),
        column3: threeColumnData.column3.map((item, index) => ({
          ...item,
          value: data.result[item.title.toLowerCase().replace(/ (.)/g, (match, letter) => letter.toUpperCase())],
        })),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isSuccess]);

  const { data: notificationData, refetch, isSuccess: notificationSuccess } = useGetNotificationQuery();
  const [notifications, setNotifications] = React.useState<{ message: string; link: string; }[]>([]);
  const [message, setMessage] = React.useState("");

  React.useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (notificationSuccess) {
      setNotifications(notificationData?.result);
      //join the notification message with icon
      const m = notificationData?.result.map((item: any) => item.message).join(" ♦ ");
      setMessage(m);

    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationData, notificationSuccess]);

  return (
    <div className={pageClassName}>
      <p className={`${pageClassName}__title`}>{PAGE_NAME.DASHBOARD}</p>
      <div className={`${pageClassName}__container`}>
        <div className={`${pageClassName}__notification`}>
          <p className={`${pageClassName}__notification__title`}>
            <Icon icon="notification" size="primary" />Alerts & Notifications
          </p>
          <ScrollingText>{message}</ScrollingText>
          <div className={`${pageClassName}__notification__content ${notifications.length === 0 ? `${pageClassName}__notification__content--empty` : ""}`}>
            {notifications.length > 0 ?
              <div className={`${pageClassName}__notification__container`}>
                {notifications.map((item: any, index: number) => (
                  <div className={`${pageClassName}__notification__item`} key={item?.message}>
                    <Icon icon="bell" size="secondary" />
                    <Link to={item?.link} className={`${pageClassName}__notification__item__message`}>
                      <p dangerouslySetInnerHTML={{ __html: item?.message }} />
                    </Link>
                  </div>
                ))}
              </div>
              :
              <div className={`${pageClassName}__notification__notFound`}>
                <Icon icon="smileFace" size="tertiary" />
                <p className={`${pageClassName}__notification__content__text`}>
                  No Alerts & Notifications Found.
                </p>
              </div>
            }
          </div>
        </div>
        {hasPermission(PERMISSIONS.DASHBOARD_ADMIN_VIEW.title) && (
          <div className={`${pageClassName}__analytics`}>
            {/* Analytics Header */}
            <div className={`${pageClassName}__analytics__header`}>
              <h2 className={`${pageClassName}__analytics__title`}>Analytics Overview</h2>
              <p className={`${pageClassName}__analytics__subtitle`}>Real-time insights and key metrics</p>
            </div>

            {/* 3-Column List Layout */}
            <div className={`${pageClassName}__three-column-list`}>
              {/* Column 1: Core Metrics */}
              <div className={`${pageClassName}__column`}>
                <div className={`${pageClassName}__column__header`}>
                  <h3 className={`${pageClassName}__column__title`}>
                    <span className={`${pageClassName}__column__icon`}>📊</span>
                    Core Metrics
                  </h3>
                </div>
                <div className={`${pageClassName}__list`}>
                  {threeColumnData.column1.map((item: any, index: number) => (
                    <div className={`${pageClassName}__list__item`} key={index}>
                      <div className={`${pageClassName}__list__item__icon`}>
                        <Icon icon={item.icon} size="primary" />
                      </div>
                      <div className={`${pageClassName}__list__item__content`}>
                        <div className={`${pageClassName}__list__item__title`}>{item.title}</div>
                        <div className={`${pageClassName}__list__item__value`}>{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Column 2: Usage & Costs */}
              <div className={`${pageClassName}__column`}>
                <div className={`${pageClassName}__column__header`}>
                  <h3 className={`${pageClassName}__column__title`}>
                    <span className={`${pageClassName}__column__icon`}>💰</span>
                    Usage & Costs
                  </h3>
                </div>
                <div className={`${pageClassName}__list`}>
                  {threeColumnData.column2.map((item: any, index: number) => (
                    <div className={`${pageClassName}__list__item`} key={index}>
                      <div className={`${pageClassName}__list__item__icon`}>
                        <Icon icon={item.icon} size="primary" />
                      </div>
                      <div className={`${pageClassName}__list__item__content`}>
                        <div className={`${pageClassName}__list__item__title`}>{item.title}</div>
                        <div className={`${pageClassName}__list__item__value`}>{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Column 3: Management Queue */}
              <div className={`${pageClassName}__column`}>
                <div className={`${pageClassName}__column__header`}>
                  <h3 className={`${pageClassName}__column__title`}>
                    <span className={`${pageClassName}__column__icon`}>⚙️</span>
                    Management Queue
                  </h3>
                </div>
                <div className={`${pageClassName}__list`}>
                  {threeColumnData.column3.map((item: any, index: number) => (
                    <div className={`${pageClassName}__list__item ${item.value > 0 ? `${pageClassName}__list__item--alert` : ''}`} key={index}>
                      <div className={`${pageClassName}__list__item__icon`}>
                        <Icon icon={item.icon} size="primary" />
                      </div>
                      <div className={`${pageClassName}__list__item__content`}>
                        <div className={`${pageClassName}__list__item__title`}>{item.title}</div>
                        <div className={`${pageClassName}__list__item__value`}>{item.value}</div>
                      </div>
                      <div className={`${pageClassName}__list__item__status`}>
                        {item.value > 0 ? '🔥' : '✅'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
